import {
  OtaUpdateStatus,
  getOtaVersionTone,
  type DroneOtaEntity,
  type OtaVersionTone,
} from '@src/core/features';

export type FirmwareBannerTone = OtaVersionTone;

export interface FirmwareBannerView {
  visible: boolean;
  tone: FirmwareBannerTone;
  message: string;
  /** Show the in-progress spinner icon instead of the warning icon. */
  showSpinner: boolean;
  /** Offer the primary Update button. */
  showUpdateButton: boolean;
}

const MESSAGES = {
  required: 'Firmware update required. Update the drone before it can be operated.',
  recommended: 'A newer firmware version is available for this drone.',
  inProgress: 'Firmware update in progress. Please keep the drone powered on until the update completes.',
  deferred: 'Firmware update postponed. Update the drone before it can be operated.',
  failed: 'Firmware update failed. Please try again.',
  timedOut: 'Firmware update timed out. Please try again.',
} as const;

const HIDDEN_VIEW: FirmwareBannerView = {
  visible: false,
  tone: 'warning',
  message: '',
  showSpinner: false,
  showUpdateButton: false,
};

/**
 * How the banner should behave for a given `lastUpdateStatus`, independent of the version verdict.
 *  - `hidden`   — nothing pending, never render (COMPLETED / CANCELLED);
 *  - `progress` — an attempt is running: spinner, no action;
 *  - `attention`— a finished attempt needs a retry: fixed message + Update button;
 *  - `verdict`  — no active attempt: message + button come from the version verdict.
 */
type FirmwareStatusBehavior =
  | { kind: 'hidden' }
  | { kind: 'progress' }
  | { kind: 'attention'; message: string }
  | { kind: 'verdict' };

const STATUS_BEHAVIOR: Record<OtaUpdateStatus, FirmwareStatusBehavior> = {
  [OtaUpdateStatus.COMPLETED]: { kind: 'hidden' },
  [OtaUpdateStatus.CANCELLED]: { kind: 'hidden' },
  [OtaUpdateStatus.REQUESTED]: { kind: 'progress' },
  [OtaUpdateStatus.IN_PROGRESS]: { kind: 'progress' },
  [OtaUpdateStatus.FAILED]: { kind: 'attention', message: MESSAGES.failed },
  [OtaUpdateStatus.TIMED_OUT]: { kind: 'attention', message: MESSAGES.timedOut },
  [OtaUpdateStatus.DEFERRED]: { kind: 'attention', message: MESSAGES.deferred },
  [OtaUpdateStatus.INITIAL]: { kind: 'verdict' },
};

// A missing or unrecognised status is treated as "no attempt yet" — pure version verdict.
function resolveStatusBehavior(status: OtaUpdateStatus | null): FirmwareStatusBehavior {
  return (status && STATUS_BEHAVIOR[status]) || { kind: 'verdict' };
}

function verdictMessage(tone: FirmwareBannerTone): string {
  return tone === 'error' ? MESSAGES.required : MESSAGES.recommended;
}

/**
 * Firmware banner is driven by two orthogonal axes:
 *  - version verdict (`updateRequired` / `updateRecommended`) — controls VISIBILITY and COLOUR;
 *    no verdict → the drone is up to date → nothing to show.
 *  - `lastUpdateStatus` — controls the MESSAGE, the in-progress spinner and the Update button.
 * `drone.status` (operational state) is deliberately NOT consulted here.
 */
export function getFirmwareBannerView(
  ota: DroneOtaEntity | undefined,
  { canUpdate }: { canUpdate: boolean },
): FirmwareBannerView {
  const tone = getOtaVersionTone(ota);
  if (tone === null) return HIDDEN_VIEW;

  const behavior = resolveStatusBehavior(ota!.lastUpdateStatus);

  switch (behavior.kind) {
  case 'hidden':
    return HIDDEN_VIEW;
  case 'progress':
    return { visible: true, tone, message: MESSAGES.inProgress, showSpinner: true, showUpdateButton: false };
  case 'attention':
    return { visible: true, tone, message: behavior.message, showSpinner: false, showUpdateButton: canUpdate };
  case 'verdict':
    return { visible: true, tone, message: verdictMessage(tone), showSpinner: false, showUpdateButton: canUpdate };
  }
}

export function getDroneVersionLabel({
  flightDispatcher,
  isError,
}: {
  flightDispatcher: DroneOtaEntity | undefined;
  isError: boolean;
}): string {
  if (isError) return 'Unable to load';
  return flightDispatcher?.currentVersion ? `v ${flightDispatcher.currentVersion}` : '—';
}
